import React, { useState } from 'react';

import { faArrowUpRightFromSquare, faCircleCheck, faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';

import confirm from 'components/confirm';
import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { deleteRequest, post } from 'helpers/api';
import { showSnackbar } from 'components/ui/snackbar/helper';

import type { Account } from './types';

interface Props {
  account: Account | null;
}

export function StripeAccountSetup({ account }: Props) {
  const [loading, setLoading] = useState(false);

  async function handleConnect() {
    setLoading(true);

    try {
      const { onboardingUrl } = await post('/integrations/stripe/user_account');
      window.location.href = onboardingUrl;
    } catch {
      showSnackbar({ text: 'Could not open Stripe.', variant: 'danger' });
      setLoading(false);
    }
  }

  async function handleDashboard() {
    setLoading(true);

    try {
      const { dashboardUrl } = await post('/integrations/stripe/user_account/dashboard');
      window.open(dashboardUrl);
    } catch {
      showSnackbar({ text: 'Could not open the Stripe dashboard.', variant: 'danger' });
      setLoading(false);
    }
  }

  async function handleDisconnect() {
    try {
      await confirm('Disconnecting removes your Stripe account from Buildout.', {
        buttonLabel: 'Disconnect',
        buttonStyle: 'danger',
        title: 'Disconnect Stripe account',
      });
    } catch {
      return;
    }

    setLoading(true);

    try {
      await deleteRequest('/integrations/stripe/user_account');
      window.location.reload();
    } catch (e) {
      showSnackbar({
        text: typeof e === 'string' ? e : 'Could not disconnect your Stripe account.',
        variant: 'danger'
      });
      setLoading(false);
    }
  }

  const connected = account?.connected ?? false;
  const payoutsEnabled = account?.payoutsEnabled ?? false;

  let buttonLabel = 'Set up payouts with Stripe';
  if (payoutsEnabled) {
    buttonLabel = 'Edit Stripe account';
  } else if (connected) {
    buttonLabel = 'Continue Stripe onboarding';
  }

  return (
    <div className="form-vertical-field-container">
      <p className="text-muted">
        Connect a Stripe account so your brokerage can pay you directly through Buildout. Stripe
        collects and verifies your identity and bank details — Buildout never sees them.
      </p>

      {payoutsEnabled ? (
        <Alert icon={faCircleCheck} severity="success">
          Your account is connected and ready to receive payouts.
        </Alert>
      ) : (
        <Alert icon={faCircleExclamation} severity="warning">
          {connected
            ? 'Your Stripe onboarding is incomplete. Finish it to start receiving payouts.'
            : 'You have not connected a Stripe payout account yet.'}
        </Alert>
      )}

      <div className="d-flex gap-2">
        <Button disabled={loading} label={buttonLabel} onClick={handleConnect} />
        {payoutsEnabled && (
          <Button
            disabled={loading}
            iconEnd={faArrowUpRightFromSquare}
            label="Open Stripe dashboard"
            variant="secondary"
            onClick={handleDashboard}
          />
        )}
        {connected && (
          <Button disabled={loading} label="Disconnect" variant="destructive" onClick={handleDisconnect} />
        )}
      </div>
    </div>
  );
}
